import type { Property } from '../mocks/properties';

// Intent types for chat and search
export type Intent =
  | 'greeting'
  | 'search_property'
  | 'price_inquiry'
  | 'location_inquiry'
  | 'property_type_inquiry'
  | 'consultation'
  | 'contact'
  | 'membership'
  | 'list_property'
  | 'account'
  | 'farewell'
  | 'thanks'
  | 'help'
  | 'unknown';

export interface ParsedQuery {
  intent: Intent;
  entities: {
    location?: string;
    propertyType?: string;
    priceMin?: number;
    priceMax?: number;
    bedrooms?: number;
    bathrooms?: number;
    amenities?: string[];
    keywords?: string[];
  };
  confidence: number;
  originalQuery: string;
}

export interface ChatResponse {
  message: string;
  suggestions?: string[];
  action?: {
    type: 'navigate' | 'search' | 'show_properties' | 'none';
    payload?: string | Record<string, unknown>;
  };
  properties?: Property[];
}

// Keywords for intent detection in both languages
const INTENT_KEYWORDS: Record<Intent, { en: string[]; ar: string[] }> = {
  greeting: {
    en: ['hello', 'hi', 'hey', 'good morning', 'good evening', 'good afternoon', 'greetings'],
    ar: ['مرحبا', 'أهلا', 'السلام عليكم', 'صباح الخير', 'مساء الخير', 'هلا'],
  },
  search_property: {
    en: ['search', 'find', 'looking for', 'want', 'need', 'show me', 'property', 'properties', 'house', 'apartment', 'villa', 'rent', 'buy'],
    ar: ['بحث', 'أبحث', 'أريد', 'احتاج', 'عقار', 'عقارات', 'شقة', 'فيلا', 'منزل', 'بيت', 'إيجار', 'شراء', 'أرني'],
  },
  price_inquiry: {
    en: ['price', 'cost', 'how much', 'expensive', 'cheap', 'budget', 'affordable', 'sar', 'riyal'],
    ar: ['سعر', 'كم', 'تكلفة', 'غالي', 'رخيص', 'ميزانية', 'ريال'],
  },
  location_inquiry: {
    en: ['location', 'where', 'area', 'city', 'riyadh', 'jeddah', 'dammam', 'mecca', 'medina', 'neighborhood'],
    ar: ['موقع', 'أين', 'منطقة', 'مدينة', 'الرياض', 'جدة', 'الدمام', 'مكة', 'المدينة', 'حي'],
  },
  property_type_inquiry: {
    en: ['type', 'kind', 'apartment', 'villa', 'studio', 'penthouse', 'duplex', 'townhouse'],
    ar: ['نوع', 'شقة', 'فيلا', 'استوديو', 'بنتهاوس', 'دوبلكس'],
  },
  consultation: {
    en: ['consultation', 'consult', 'book', 'appointment', 'meeting', 'advisor', 'expert', 'help me find'],
    ar: ['استشارة', 'حجز', 'موعد', 'اجتماع', 'مستشار', 'خبير', 'ساعدني'],
  },
  contact: {
    en: ['contact', 'call', 'phone', 'email', 'reach', 'support', 'customer service', 'talk to'],
    ar: ['تواصل', 'اتصال', 'هاتف', 'بريد', 'دعم', 'خدمة العملاء', 'تكلم'],
  },
  membership: {
    en: ['membership', 'member', 'subscribe', 'subscription', 'plan', 'premium', 'vip'],
    ar: ['عضوية', 'اشتراك', 'خطة', 'بريميوم', 'مميز'],
  },
  list_property: {
    en: ['list', 'add', 'post', 'sell', 'rent out', 'my property', 'advertise'],
    ar: ['إضافة', 'أضف', 'نشر', 'بيع', 'تأجير', 'عقاري', 'إعلان'],
  },
  account: {
    en: ['account', 'profile', 'settings', 'login', 'sign in', 'sign up', 'register', 'password'],
    ar: ['حساب', 'ملف', 'إعدادات', 'تسجيل', 'دخول', 'تسجيل'],
  },
  farewell: {
    en: ['bye', 'goodbye', 'see you', 'later', 'thanks bye', 'quit', 'exit'],
    ar: ['مع السلامة', 'وداعا', 'إلى اللقاء', 'باي'],
  },
  thanks: {
    en: ['thank', 'thanks', 'appreciate', 'helpful', 'great'],
    ar: ['شكرا', 'شكر', 'ممتاز', 'رائع'],
  },
  help: {
    en: ['help', 'how', 'what', 'can you', 'assist', 'guide', 'explain'],
    ar: ['مساعدة', 'كيف', 'ماذا', 'هل يمكنك', 'ساعدني', 'شرح'],
  },
  unknown: { en: [], ar: [] },
};

// Location keywords
const LOCATIONS: { en: string[]; ar: string[] } = {
  en: ['riyadh', 'jeddah', 'dammam', 'mecca', 'medina', 'khobar', 'dhahran', 'taif', 'tabuk', 'abha'],
  ar: ['الرياض', 'جدة', 'الدمام', 'مكة', 'المدينة', 'الخبر', 'الظهران', 'الطائف', 'تبوك', 'أبها'],
};

// Property type keywords
const PROPERTY_TYPES: { en: string[]; ar: string[] } = {
  en: ['apartment', 'villa', 'studio', 'penthouse', 'duplex', 'townhouse', 'house', 'flat'],
  ar: ['شقة', 'فيلا', 'استوديو', 'بنتهاوس', 'دوبلكس', 'منزل', 'بيت'],
};

// Amenity keywords
const AMENITIES: { en: string[]; ar: string[] } = {
  en: ['pool', 'swimming', 'gym', 'parking', 'garden', 'balcony', 'security', 'elevator', 'furnished', 'pet', 'air conditioning', 'wifi'],
  ar: ['مسبح', 'سباحة', 'جيم', 'موقف', 'حديقة', 'شرفة', 'أمن', 'مصعد', 'مفروش', 'حيوانات', 'تكييف', 'واي فاي'],
};

/**
 * Detect the language of the input text
 */
export function detectLanguage(text: string): 'en' | 'ar' {
  const arabicPattern = /[\u0600-\u06FF]/;
  return arabicPattern.test(text) ? 'ar' : 'en';
}

/**
 * Detect intent from user message
 */
export function detectIntent(message: string): { intent: Intent; confidence: number } {
  const normalizedMessage = message.toLowerCase().trim();
  const lang = detectLanguage(message);

  let bestIntent: Intent = 'unknown';
  let bestScore = 0;

  for (const [intent, keywords] of Object.entries(INTENT_KEYWORDS)) {
    const langKeywords = keywords[lang];
    let score = 0;

    for (const keyword of langKeywords) {
      if (normalizedMessage.includes(keyword.toLowerCase())) {
        score += keyword.length / normalizedMessage.length;
      }
    }

    if (score > bestScore) {
      bestScore = score;
      bestIntent = intent as Intent;
    }
  }

  // Boost confidence if multiple keywords match
  const confidence = Math.min(bestScore * 100, 100);

  return { intent: bestIntent, confidence };
}

/**
 * Extract entities from user message (locations, property types, prices, etc.)
 */
export function extractEntities(message: string): ParsedQuery['entities'] {
  const lang = detectLanguage(message);
  const normalizedMessage = message.toLowerCase();
  const entities: ParsedQuery['entities'] = {};

  // Extract location
  const locations = LOCATIONS[lang];
  for (const location of locations) {
    if (normalizedMessage.includes(location.toLowerCase())) {
      entities.location = location;
      break;
    }
  }

  // Extract property type
  const propertyTypes = PROPERTY_TYPES[lang];
  for (const type of propertyTypes) {
    if (normalizedMessage.includes(type.toLowerCase())) {
      entities.propertyType = type;
      break;
    }
  }

  // Extract price range
  const pricePattern = /(\d{1,3}(?:,\d{3})*|\d+)(?:\s*(?:sar|ريال|riyal|sr|ر\.س))?/gi;
  const prices = [...normalizedMessage.matchAll(pricePattern)].map(m => parseInt(m[1].replace(/,/g, '')));
  if (prices.length >= 2) {
    entities.priceMin = Math.min(...prices);
    entities.priceMax = Math.max(...prices);
  } else if (prices.length === 1) {
    // Check context for min/max
    if (normalizedMessage.includes('under') || normalizedMessage.includes('less') || normalizedMessage.includes('أقل') || normalizedMessage.includes('تحت')) {
      entities.priceMax = prices[0];
    } else if (normalizedMessage.includes('above') || normalizedMessage.includes('more') || normalizedMessage.includes('أكثر') || normalizedMessage.includes('فوق')) {
      entities.priceMin = prices[0];
    } else {
      entities.priceMax = prices[0];
    }
  }

  // Extract bedrooms
  const bedroomPatterns = [
    /(\d+)\s*(?:bedroom|bed|br|غرف|غرفة)/i,
    /(\d+)\s*(?:غرف نوم|غرفة نوم)/i,
  ];
  for (const pattern of bedroomPatterns) {
    const match = normalizedMessage.match(pattern);
    if (match) {
      entities.bedrooms = parseInt(match[1]);
      break;
    }
  }

  // Extract bathrooms
  const bathroomPatterns = [
    /(\d+)\s*(?:bathroom|bath|ba|دورات مياه|دورة مياه|حمام|حمامات)/i,
  ];
  for (const pattern of bathroomPatterns) {
    const match = normalizedMessage.match(pattern);
    if (match) {
      entities.bathrooms = parseInt(match[1]);
      break;
    }
  }

  // Extract amenities
  const amenities = AMENITIES[lang];
  const foundAmenities: string[] = [];
  for (const amenity of amenities) {
    if (normalizedMessage.includes(amenity.toLowerCase())) {
      foundAmenities.push(amenity);
    }
  }
  if (foundAmenities.length > 0) {
    entities.amenities = foundAmenities;
  }

  return entities;
}

/**
 * Parse a natural language query into structured search parameters
 */
export function parseNaturalLanguageQuery(query: string): ParsedQuery {
  const { intent, confidence } = detectIntent(query);
  const entities = extractEntities(query);

  return {
    intent,
    entities,
    confidence,
    originalQuery: query,
  };
}

/**
 * Generate AI response for chatbot
 */
export function generateChatResponse(
  message: string,
  lang: 'en' | 'ar',
  _properties?: Property[]
): ChatResponse {
  const { intent, entities } = parseNaturalLanguageQuery(message);

  // Default suggestions based on language
  const defaultSuggestions = lang === 'ar'
    ? ['البحث عن عقار', 'حجز استشارة', 'معلومات الأسعار', 'تواصل معنا']
    : ['Search properties', 'Book consultation', 'Pricing info', 'Contact us'];

  switch (intent) {
    case 'greeting':
      return {
        message: lang === 'ar'
          ? 'مرحباً بك في صفقات 24! أنا هنا لمساعدتك في العثور على عقارك المثالي. كيف يمكنني مساعدتك اليوم؟'
          : 'Welcome to Safaqat 24! I\'m here to help you find your perfect property. How can I help you today?',
        suggestions: defaultSuggestions,
        action: { type: 'none' },
      };

    case 'search_property': {
      const searchSuggestions = lang === 'ar'
        ? ['شقق في الرياض', 'فلل في جدة', 'استوديو مفروش', 'عقارات بمسبح']
        : ['Apartments in Riyadh', 'Villas in Jeddah', 'Furnished studio', 'Properties with pool'];

      if (Object.keys(entities).length > 0) {
        // User provided search criteria
        return {
          message: lang === 'ar'
            ? 'رائع! سأبحث لك عن عقارات تناسب متطلباتك. دعني أوجهك إلى صفحة البحث مع الفلاتر المناسبة.'
            : 'Great! I\'ll search for properties matching your requirements. Let me direct you to the search page with the appropriate filters.',
          suggestions: searchSuggestions,
          action: {
            type: 'search',
            payload: entities,
          },
        };
      }

      return {
        message: lang === 'ar'
          ? 'يمكنني مساعدتك في البحث عن العقارات! أخبرني ما الذي تبحث عنه - الموقع، نوع العقار، نطاق السعر، أو عدد الغرف.'
          : 'I can help you search for properties! Tell me what you\'re looking for - location, property type, price range, or number of rooms.',
        suggestions: searchSuggestions,
        action: { type: 'none' },
      };
    }

    case 'price_inquiry':
      return {
        message: lang === 'ar'
          ? 'تتراوح عقاراتنا من الخيارات الاقتصادية إلى الفاخرة. يمكنك العثور على شقق بدءاً من 2,000 ريال/شهر وفلل فاخرة تصل إلى 50,000 ريال/شهر. ما هو نطاق ميزانيتك؟'
          : 'Our properties range from budget-friendly to luxury options. You can find apartments starting from 2,000 SAR/month and luxury villas up to 50,000 SAR/month. What\'s your budget range?',
        suggestions: lang === 'ar'
          ? ['أقل من 5,000 ريال', '5,000 - 15,000 ريال', '15,000 - 30,000 ريال', 'أكثر من 30,000 ريال']
          : ['Under 5,000 SAR', '5,000 - 15,000 SAR', '15,000 - 30,000 SAR', 'Above 30,000 SAR'],
        action: { type: 'none' },
      };

    case 'location_inquiry':
      return {
        message: lang === 'ar'
          ? 'لدينا عقارات في المدن السعودية الرئيسية بما في ذلك الرياض وجدة والدمام والخبر. أي مدينة أو منطقة تهمك؟'
          : 'We have properties in major Saudi cities including Riyadh, Jeddah, Dammam, and Khobar. Which city or area interests you?',
        suggestions: lang === 'ar'
          ? ['الرياض', 'جدة', 'الدمام', 'الخبر']
          : ['Riyadh', 'Jeddah', 'Dammam', 'Khobar'],
        action: { type: 'none' },
      };

    case 'property_type_inquiry':
      return {
        message: lang === 'ar'
          ? 'نقدم أنواع عقارات متنوعة تشمل الشقق والفلل والاستوديوهات والبنتهاوس والدوبلكس. ما نوع العقار الذي تفضله؟'
          : 'We offer various property types including apartments, villas, studios, penthouses, and duplexes. What type of property do you prefer?',
        suggestions: lang === 'ar'
          ? ['شقة', 'فيلا', 'استوديو', 'بنتهاوس']
          : ['Apartment', 'Villa', 'Studio', 'Penthouse'],
        action: { type: 'none' },
      };

    case 'consultation':
      return {
        message: lang === 'ar'
          ? 'يسعدنا تقديم استشارة مجانية مع خبرائنا العقاريين! يمكنهم مساعدتك في العثور على العقار المثالي وفقاً لاحتياجاتك. هل ترغب في حجز موعد؟'
          : 'We\'d be happy to offer a free consultation with our real estate experts! They can help you find the perfect property based on your needs. Would you like to book an appointment?',
        suggestions: lang === 'ar'
          ? ['نعم، احجز لي', 'أخبرني المزيد', 'ما هي الخدمات المتوفرة؟']
          : ['Yes, book for me', 'Tell me more', 'What services are available?'],
        action: {
          type: 'navigate',
          payload: '/consultation',
        },
      };

    case 'contact':
      return {
        message: lang === 'ar'
          ? 'يمكنك التواصل مع فريقنا عبر:\n📧 البريد: info@safaqat24.sa\n📞 الهاتف: +966 50 560 8271\n📍 العنوان: جدة\n\nأو يمكنك زيارة صفحة التواصل لإرسال رسالة مباشرة.'
          : 'You can reach our team via:\n📧 Email: info@safaqat24.sa\n📞 Phone: +966 50 560 8271\n📍 Address: Jeddah\n\nOr you can visit our contact page to send a direct message.',
        suggestions: lang === 'ar'
          ? ['زيارة صفحة التواصل', 'حجز استشارة', 'عرض العقارات']
          : ['Visit contact page', 'Book consultation', 'View properties'],
        action: {
          type: 'navigate',
          payload: '/contact',
        },
      };

    case 'membership':
      return {
        message: lang === 'ar'
          ? 'نقدم خطط عضوية متنوعة مع مزايا حصرية تشمل الوصول المبكر للعقارات الجديدة، خصومات خاصة، واستشارات مجانية. هل تريد معرفة المزيد عن خطط العضوية؟'
          : 'We offer various membership plans with exclusive benefits including early access to new properties, special discounts, and free consultations. Would you like to learn more about our membership plans?',
        suggestions: lang === 'ar'
          ? ['عرض الخطط', 'ما هي المزايا؟', 'كيف أشترك؟']
          : ['View plans', 'What are the benefits?', 'How do I subscribe?'],
        action: {
          type: 'navigate',
          payload: '/membership',
        },
      };

    case 'list_property':
      return {
        message: lang === 'ar'
          ? 'هل تريد إضافة عقارك للإيجار أو البيع؟ يمكنك إضافة عقارك بسهولة من خلال صفحة إضافة العقار. سيراجع فريقنا العقار ويتم نشره خلال 24 ساعة.'
          : 'Want to list your property for rent or sale? You can easily add your property through our listing page. Our team will review it and publish it within 24 hours.',
        suggestions: lang === 'ar'
          ? ['إضافة عقار', 'ما هي الرسوم؟', 'كيف تعمل العملية؟']
          : ['Add property', 'What are the fees?', 'How does it work?'],
        action: {
          type: 'navigate',
          payload: '/properties/new',
        },
      };

    case 'account':
      return {
        message: lang === 'ar'
          ? 'يمكنك إدارة حسابك من صفحة الملف الشخصي. هناك يمكنك تعديل بياناتك، عرض عقاراتك المفضلة، وإدارة إعداداتك.'
          : 'You can manage your account from the profile page. There you can edit your information, view your favorite properties, and manage your settings.',
        suggestions: lang === 'ar'
          ? ['الذهاب للملف الشخصي', 'تسجيل الدخول', 'إنشاء حساب']
          : ['Go to profile', 'Sign in', 'Create account'],
        action: {
          type: 'navigate',
          payload: '/profile',
        },
      };

    case 'farewell':
      return {
        message: lang === 'ar'
          ? 'شكراً لتواصلك مع صفقات 24! نتمنى لك يوماً سعيداً. لا تتردد في العودة إذا كان لديك أي أسئلة أخرى. 👋'
          : 'Thank you for chatting with Safaqat 24! Have a wonderful day. Feel free to come back if you have any other questions. 👋',
        action: { type: 'none' },
      };

    case 'thanks':
      return {
        message: lang === 'ar'
          ? 'على الرحب والسعة! هل هناك شيء آخر يمكنني مساعدتك به؟'
          : 'You\'re welcome! Is there anything else I can help you with?',
        suggestions: defaultSuggestions,
        action: { type: 'none' },
      };

    case 'help':
      return {
        message: lang === 'ar'
          ? 'أنا هنا للمساعدة! يمكنني مساعدتك في:\n• البحث عن العقارات\n• معلومات الأسعار\n• حجز الاستشارات\n• التواصل مع فريقنا\n• معلومات العضوية\n\nما الذي تحتاج مساعدة فيه؟'
          : 'I\'m here to help! I can assist you with:\n• Searching for properties\n• Price information\n• Booking consultations\n• Contacting our team\n• Membership information\n\nWhat do you need help with?',
        suggestions: defaultSuggestions,
        action: { type: 'none' },
      };

    default:
      return {
        message: lang === 'ar'
          ? 'شكراً على رسالتك! لست متأكداً تماماً مما تبحث عنه. هل يمكنك إعادة صياغة سؤالك؟ يمكنك الاستفسار عن العقارات، الأسعار، الاستشارات، أو التواصل معنا.'
          : 'Thanks for your message! I\'m not quite sure what you\'re looking for. Could you rephrase your question? You can ask about properties, pricing, consultations, or contacting us.',
        suggestions: defaultSuggestions,
        action: { type: 'none' },
      };
  }
}

/**
 * Convert search entities to URL search params
 */
export function entitiesToSearchParams(entities: ParsedQuery['entities']): URLSearchParams {
  const params = new URLSearchParams();

  if (entities.location) {
    params.set('query', entities.location);
  }
  if (entities.propertyType) {
    params.set('propertyType', entities.propertyType);
  }
  if (entities.priceMin) {
    params.set('minPrice', entities.priceMin.toString());
  }
  if (entities.priceMax) {
    params.set('maxPrice', entities.priceMax.toString());
  }
  if (entities.bedrooms) {
    params.set('bedrooms', entities.bedrooms.toString());
  }
  if (entities.bathrooms) {
    params.set('bathrooms', entities.bathrooms.toString());
  }

  return params;
}

/**
 * Get AI-powered search suggestions based on partial query
 */
export function getAISearchSuggestions(
  query: string,
  properties: Property[],
  lang: 'en' | 'ar'
): string[] {
  const suggestions: string[] = [];
  const normalizedQuery = query.toLowerCase().trim();

  if (!normalizedQuery) {
    // Return popular suggestions
    return lang === 'ar'
      ? ['شقق في الرياض', 'فلل فاخرة', 'عقارات مفروشة', 'إيجار شهري']
      : ['Apartments in Riyadh', 'Luxury villas', 'Furnished properties', 'Monthly rent'];
  }

  // Extract what the user might be looking for
  const { entities } = parseNaturalLanguageQuery(query);

  // Generate contextual suggestions
  if (entities.location) {
    const location = entities.location;
    suggestions.push(
      lang === 'ar' ? `شقق في ${location}` : `Apartments in ${location}`,
      lang === 'ar' ? `فلل في ${location}` : `Villas in ${location}`,
      lang === 'ar' ? `${location} مفروش` : `Furnished in ${location}`
    );
  }

  if (entities.propertyType) {
    const type = entities.propertyType;
    suggestions.push(
      lang === 'ar' ? `${type} للإيجار` : `${type} for rent`,
      lang === 'ar' ? `${type} فاخرة` : `Luxury ${type}`,
      lang === 'ar' ? `${type} مع مسبح` : `${type} with pool`
    );
  }

  if (entities.priceMax) {
    suggestions.push(
      lang === 'ar' ? `عقارات أقل من ${entities.priceMax} ريال` : `Properties under ${entities.priceMax} SAR`
    );
  }

  // Add some variety based on available properties
  const cities = [...new Set(properties.map(p => p.city))].slice(0, 3);
  cities.forEach(city => {
    if (!suggestions.some(s => s.includes(city))) {
      suggestions.push(lang === 'ar' ? `عقارات في ${city}` : `Properties in ${city}`);
    }
  });

  return suggestions.slice(0, 5);
}
